/* eslint-disable @typescript-eslint/no-explicit-any */
import { trpc } from '../../api/trpc';
import { ParameterRow } from './ParameterRow';
import { PromptTemplate } from './PromptTemplate';
import { TriggerCard } from './TriggerCard';
import { RunItem } from './RunItem';

interface Props {
  agentId: string;
  onBack: () => void;
}

export function AgentDetailInline({ agentId, onBack }: Props) {
  const { data: agent } = trpc.taskAgents.agentGet.useQuery({ agentId });

  if (!agent) {
    return (
      <div className="page-scroll">
        <div className="page-inner">
          <button className="page-back-btn" onClick={onBack}>&larr;</button>
          <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Loading agent...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-scroll">
      <div className="page-inner">
        {/* Header */}
        <div className="agent-header">
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <button className="page-back-btn" onClick={onBack} style={{ marginTop: 4 }}>&larr;</button>
            <div className={`agent-header-icon ${agent.isPreBuilt ? 'prebuilt' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="agent-detail-title">{agent.name}</span>
                <span className="agent-type-badge">{agent.isPreBuilt ? 'Pre-built' : 'Custom'}</span>
              </div>
              <div className="agent-detail-desc">{agent.description}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  className="btn-primary"
                  style={{ padding: '6px 14px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                  Run
                </button>
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  by {agent.creatorName ?? 'Unknown'} · v{agent.version}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Parameters */}
        {agent.parameters?.length > 0 && (
          <>
            <div className="agent-section-title">Parameters</div>
            {agent.parameters.map((p: any) => (
              <ParameterRow
                key={p.id}
                name={p.name}
                type={p.type}
                description={p.description}
                required={p.required}
                defaultValue={p.defaultValue}
              />
            ))}
          </>
        )}

        {/* Prompt Template */}
        <div className="agent-section-title mt">Prompt Template</div>
        <PromptTemplate template={agent.prompt} />

        {/* Triggers */}
        {agent.triggers?.length > 0 && (
          <>
            <div className="agent-section-title mt">Triggers</div>
            {agent.triggers.map((t: any) => (
              <TriggerCard key={t.id} trigger={t} />
            ))}
          </>
        )}

        {/* Recent Runs */}
        {agent.recentRuns?.length > 0 && (
          <>
            <div className="agent-section-title mt">Recent Runs</div>
            {agent.recentRuns.map((r: any) => (
              <RunItem key={r.id} run={r} />
            ))}
          </>
        )}

        {/* Shared With */}
        {agent.shares?.length > 0 && (
          <>
            <div className="agent-section-title mt">Shared With</div>
            {agent.shares.map((s: any) => (
              <div key={s.id} className="param" style={{ gap: 8 }}>
                <span className="param-name" style={{ minWidth: 'auto' }}>{s.userId ?? 'All users'}</span>
                <span className="param-type">{s.permission}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
